"use client";

import { useEffect, useMemo, useState } from "react";
import { sanitizeHtml } from "@/utils/sanitize";
import { FeedNewsItem, NewsBgColor } from "@/types/NewsTypes";

/**
 * Map bg_color -> Tailwind classes for the modal body.
 * Soft tints: 50-shade background with a slightly stronger border.
 */
const bgClassFor = (color: NewsBgColor): string => {
  switch (color) {
    case "red":
      return "bg-red-50 border border-red-200";
    case "orange":
      return "bg-orange-50 border border-orange-200";
    case "green":
      return "bg-green-50 border border-green-200";
    default:
      return "bg-white";
  }
};

interface NewsModalProps {
  item: FeedNewsItem;
  /** "popup" = post-login pop-up (dismissible after timer). "blocking" = maintenance gate (must acknowledge). */
  mode: "popup" | "blocking";
  onAccept?: () => void;
  onClose?: () => void;
}

/**
 * Modal used for both pop-up and blocking news.
 *
 * Behaviour:
 *   - The accept/close button is disabled until `popup_min_seconds` elapsed.
 *   - "popup" mode shows a close (X) icon (disabled-then-enabled) plus an OK button.
 *   - "blocking" mode hides the close icon and only enables the OK button.
 *
 * The countdown is local state; once we render the item we start ticking.
 */
export default function NewsModal({ item, mode, onAccept, onClose }: NewsModalProps) {
  const minSeconds = Math.max(0, item.popup_min_seconds ?? 0);
  const [remaining, setRemaining] = useState(minSeconds);

  useEffect(() => {
    if (minSeconds === 0) return;
    const id = setInterval(() => {
      setRemaining(prev => (prev <= 1 ? 0 : prev - 1));
    }, 1000);
    return () => clearInterval(id);
  }, [minSeconds]);

  const canDismiss = remaining === 0;

  const html = useMemo(() => {
    if (item.html_body) return sanitizeHtml(item.html_body);
    if (item.body) return sanitizeHtml(escapePlainTextToHtml(item.body));
    return "";
  }, [item.html_body, item.body]);

  const handleAccept = () => {
    if (!canDismiss) return;
    onAccept?.();
  };

  const handleClose = () => {
    if (!canDismiss) return;
    onClose?.();
  };

  return (
    <div className="fixed inset-0 bg-black/60 flex justify-center items-center z-[60]">
      <div
        className={`${bgClassFor(item.bg_color)} rounded-lg p-5 w-[640px] max-w-[92vw] max-h-[90dvh] overflow-hidden flex flex-col gap-4 shadow-2xl`}
      >
        <div className="flex justify-between items-start gap-4">
          <h2 className="text-2xl font-bold">{item.title || ""}</h2>
          {mode === "popup" && (
            <button
              type="button"
              className={`text-2xl font-bold ${
                canDismiss ? "cursor-pointer" : "cursor-not-allowed text-gray-300"
              }`}
              onClick={handleClose}
              disabled={!canDismiss}
              aria-label="Close"
            >
              ×
            </button>
          )}
        </div>

        <div
          className="text-gray-800 text-sm overflow-y-auto"
          dangerouslySetInnerHTML={{ __html: html }}
        />

        {item._translatedFromFallback && (
          <p className="text-[11px] text-gray-400 italic">
            Translated from {item._translatedFromFallback}
          </p>
        )}

        <div className="flex justify-end items-center gap-3 pt-2">
          {!canDismiss && <span className="text-xs text-gray-500">{remaining}s</span>}
          <button
            type="button"
            onClick={handleAccept}
            disabled={!canDismiss}
            className={`px-4 py-1.5 rounded text-white text-sm font-medium ${
              canDismiss ? "bg-blue-600 hover:bg-blue-700" : "bg-gray-300 cursor-not-allowed"
            }`}
          >
            OK
          </button>
        </div>
      </div>
    </div>
  );
}

function escapePlainTextToHtml(text: string): string {
  return text
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/\n/g, "<br/>");
}
